import { useState, useEffect, useRef, useCallback } from 'react';
import { Usd, PriceCaption } from '@/utils/CurrencyContext';
import { useNavigate } from 'react-router-dom';
import { CourseVisual } from '@/components/CourseVisual';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { AuthModal } from '@/components/AuthModal';
import { RoleSelectionModal } from '@/components/RoleSelectionModal';
import AnnouncementRibbon from '@/components/AnnouncementRibbon';
import Footer from '@/components/Footer';
import ContactSupportDialog from '@/components/ContactSupportDialog';
import { Video, Brain, Award, Users, BookOpen, Play, ChevronRight, Info, Mail, MessageCircle, X, ShoppingBag, Package, Sparkles, Clock, AlertTriangle, Search, Loader2, HelpCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { isAuthenticated, getUser } from '@/utils/auth';

const API = process.env.REACT_APP_BACKEND_URL;

const LandingPage = () => {
  const [showAuth, setShowAuth] = useState(false);
  const [showRoleSelection, setShowRoleSelection] = useState(false); // Role selection modal
  const [authDefaultRole, setAuthDefaultRole] = useState('student'); // Track which role to pre-select
  const [showExitConfirm, setShowExitConfirm] = useState(false); // Exit confirmation dialog
  const navigate = useNavigate();

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const searchTimeoutRef = useRef(null);

  // Ribbon auto-scroll refs and state
  const ribbonRef = useRef(null);
  const [isRibbonPaused, setIsRibbonPaused] = useState(false);
  
  // Track if we should show exit confirmation (only on landing page, not when navigating internally)
  const isExitAttemptRef = useRef(false);

  // Handle browser back button / swipe back - Exit confirmation
  useEffect(() => {
    // Push a dummy state to detect back navigation
    const pushDummyState = () => {
      window.history.pushState({ exitGuard: true }, '', window.location.href);
    };
    
    // Initial push
    pushDummyState();
    
    const handlePopState = (event) => {
      // Only show confirmation if we're on the landing page and it's an exit attempt
      if (window.location.pathname === '/' && !isExitAttemptRef.current) {
        // Prevent the back navigation temporarily
        pushDummyState();
        // Show the exit confirmation dialog
        setShowExitConfirm(true);
      }
    };
    
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Handle exit confirmation
  const handleExitConfirm = useCallback(() => {
    isExitAttemptRef.current = true;
    setShowExitConfirm(false);
    // Go back twice - once for our dummy state, once for actual back
    window.history.go(-2);
  }, []);

  const handleExitCancel = useCallback(() => {
    setShowExitConfirm(false);
  }, []);

  // Search functionality with debounce
  const handleSearch = useCallback(async (query) => {
    if (!query.trim()) {
      setSearchResults(null);
      setShowSearchResults(false);
      return;
    }
    
    setIsSearching(true);
    setShowSearchResults(true);
    
    try {
      const response = await fetch(`${API}/api/search?q=${encodeURIComponent(query.trim())}`);
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data);
      }
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Debounced search on input change
  const handleSearchInputChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Debounce search by 300ms
    searchTimeoutRef.current = setTimeout(() => {
      handleSearch(query);
    }, 300);
  };

  // Clear search
  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults(null);
    setShowSearchResults(false);
  };

  // Numecis logo for V2 and V3 courses
  const numecisLogo = 'https://customer-assets.emergentagent.com/job_edu-platform-304/artifacts/gyac8usc_23228.jpg';

  // Slot configurations from admin
  const [slotConfigs, setSlotConfigs] = useState({
    ribbon: { slots: [], total_slots: 8 },
    featured: { slots: [], total_slots: 6 },
    new_arrivals: { slots: [], total_slots: 6 }
  });
  const [loadingSlots, setLoadingSlots] = useState(true);

  // Landing page stats
  const [landingStats, setLandingStats] = useState({
    views: '300K+',
    enrollments: '500+'
  });

  // Teacher announcements
  const [announcements, setAnnouncements] = useState([]);

  // Fetch slot configurations
  useEffect(() => {
    const fetchSlots = async () => {
      try {
        const res = await fetch(`${API}/api/landing-slots`);
        if (res.ok) {
          const data = await res.json();
          setSlotConfigs(data);
        }
      } catch (err) {
        console.error('Failed to fetch slot configs:', err);
      } finally {
        setLoadingSlots(false);
      }
    };
    fetchSlots();
  }, []);

  // Fetch landing stats
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await fetch(`${API}/api/landing-stats`);
        if (res.ok) {
          const data = await res.json();
          setLandingStats(data);
        }
      } catch (err) {
        console.error('Failed to fetch stats:', err);
      }
    };
    fetchStats();
  }, []);

  // Fetch teacher announcements
  useEffect(() => {
    const fetchAnnouncements = async () => {
      try {
        const res = await fetch(`${API}/api/announcements/approved`);
        if (res.ok) {
          const data = await res.json();
          setAnnouncements(data);
        }
      } catch (err) {
        console.error('Failed to fetch announcements:', err);
      }
    };
    fetchAnnouncements();
  }, []);

  // Teacher galleries
  const [teacherGalleries, setTeacherGalleries] = useState([]);
  const [loadingGalleries, setLoadingGalleries] = useState(true);

  // Fetch teacher galleries
  useEffect(() => {
    const fetchTeacherGalleries = async () => {
      try {
        const res = await fetch(`${API}/api/gallery/public-teachers`);
        if (res.ok) {
          const data = await res.json();
          setTeacherGalleries(data.teachers || []);
        }
      } catch (err) {
        console.error('Failed to fetch teacher galleries:', err);
      } finally {
        setLoadingGalleries(false);
      }
    };
    fetchTeacherGalleries();
  }, []);

  // Pause/resume ribbon animation on user interaction
  const resumeTimeoutRef = useRef(null);
  
  const handleRibbonInteractionStart = () => {
    setIsRibbonPaused(true);
    if (resumeTimeoutRef.current) {
      clearTimeout(resumeTimeoutRef.current);
    }
  };

  const handleRibbonInteractionEnd = () => {
    if (resumeTimeoutRef.current) {
      clearTimeout(resumeTimeoutRef.current);
    }
    resumeTimeoutRef.current = setTimeout(() => {
      setIsRibbonPaused(false);
    }, 2500); // Resume after 2.5 seconds
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (resumeTimeoutRef.current) {
        clearTimeout(resumeTimeoutRef.current);
      }
    };
  }, []);

  // Get active slots for each section
  const ribbonItems = (slotConfigs.ribbon?.slots || [])
    .filter(slot => slot.is_active && slot.custom_title)
    .map(slot => ({
      id: slot.slot_number,
      title: slot.custom_title,
      subtitle: slot.custom_subtitle || '',
      image: slot.custom_image,
      price: slot.custom_price,
      type: slot.product_type || 'physical',
      link: slot.custom_link && slot.custom_link.trim() !== '' ? slot.custom_link : null
    }));

  const featuredProducts = (slotConfigs.featured?.slots || [])
    .filter(slot => slot.is_active && slot.custom_title);

  const newArrivals = (slotConfigs.new_arrivals?.slots || [])
    .filter(slot => slot.is_active && slot.custom_title);

  const handleAuthSuccess = (user) => {
    if (user.role === 'admin') {
      navigate('/admin/dashboard');
    } else if (user.role === 'teacher') {
      navigate('/teacher/dashboard');
    } else if (user.role === 'seller') {
      navigate('/seller/dashboard');
    } else {
      navigate('/student/dashboard');
    }
  };

  const handleGetStarted = (role = null) => {
    if (isAuthenticated()) {
      const user = getUser();
      if (user.role === 'admin') {
        navigate('/admin/dashboard');
      } else if (user.role === 'teacher') {
        navigate('/teacher/dashboard');
      } else if (user.role === 'seller') {
        navigate('/seller/dashboard');
      } else {
        navigate('/student/dashboard');
      }
    } else {
      // If role is provided (from direct button like "Teacher" or "Learner's Portal"), go straight to auth
      if (role) {
        setAuthDefaultRole(role);
        setShowAuth(true);
      } else {
        // Show role selection modal for "Get Started" button
        setShowRoleSelection(true);
      }
    }
  };

  // Handle role selection from the modal
  const handleRoleSelected = (selectedRole) => {
    setShowRoleSelection(false);
    setAuthDefaultRole(selectedRole);
    setShowAuth(true);
  };

  const features = [
    {
      icon: <Video className="h-8 w-8" style={{ color: '#163329' }} />,
      title: 'Interactive Video Lessons',
      description: 'High-quality video content with support for uploads and YouTube integration',
    },
    {
      icon: <Brain className="h-8 w-8" style={{ color: '#D86B47' }} />,
      title: 'AI-Powered Tutoring',
      description: 'Get instant help from our advanced AI tutor, available 24/7',
    },
    {
      icon: <Award className="h-8 w-8" style={{ color: '#E2A761' }} />,
      title: 'Quizzes & Assessments',
      description: 'Test your knowledge with interactive quizzes and get instant feedback',
    },
    {
      icon: <Users className="h-8 w-8" style={{ color: '#163329' }} />,
      title: 'Live Sessions',
      description: 'Connect with instructors through scheduled live video sessions',
    },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FAF9F6' }}>
      {/* Hero Section */}
      <section className="relative pt-6 sm:pt-8 pb-5 px-4 sm:px-6" data-testid="hero-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col items-center justify-center">
            <motion.div
              className="w-full max-w-3xl mx-auto text-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4 }}
              style={{ willChange: 'opacity' }}
            >
              <img
                src="https://customer-assets.emergentagent.com/job_edu-platform-304/artifacts/qpqxb694_1002523271.png"
                alt="Insight Evolution"
                className="w-full max-w-xs sm:max-w-md md:max-w-lg mx-auto mb-2"
                data-testid="hero-title"
              />
              <h2
                className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-semibold tracking-tight mb-4 sm:mb-6 text-center px-2"
                style={{ fontFamily: 'Spectral, serif', color: '#163329' }}
              >
                Home Skill Learning and Development Solution
              </h2>
              <p
                className="text-base sm:text-lg md:text-xl mb-6 sm:mb-8 px-2 text-center"
                style={{ color: '#5C635A', lineHeight: '1.7' }}
              >
                Your platform and solution for home skill learning and development
              </p>
              
              {/* Search Bar */}
              <div className="relative max-w-xl mx-auto mb-6 px-4">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Search courses and products by title or topic..."
                    value={searchQuery}
                    onChange={handleSearchInputChange}
                    className="pl-12 pr-10 h-12 text-base rounded-full border-2"
                    style={{ borderColor: '#163329', backgroundColor: 'white' }}
                    data-testid="landing-search-input"
                  />
                  {searchQuery && (
                    <button
                      onClick={clearSearch}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  )}
                </div>
                
                {/* Search Results Dropdown */}
                {showSearchResults && (
                  <div 
                    className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-lg border max-h-96 overflow-y-auto z-50"
                    style={{ borderColor: '#E3E1DB' }}
                  >
                    {isSearching ? (
                      <div className="p-6 text-center">
                        <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" style={{ color: '#163329' }} />
                        <p style={{ color: '#5C635A' }}>Searching...</p>
                      </div>
                    ) : searchResults ? (
                      <div>
                        {searchResults.total === 0 ? (
                          <div className="p-6 text-center">
                            <Search className="h-8 w-8 mx-auto mb-2" style={{ color: '#5C635A' }} />
                            <p style={{ color: '#5C635A' }}>No results found for "{searchQuery}"</p>
                          </div>
                        ) : (
                          <>
                            {/* Courses Results */}
                            {searchResults.courses && searchResults.courses.length > 0 && (
                              <div className="p-3">
                                <h4 className="text-xs font-semibold uppercase px-2 mb-2" style={{ color: '#5C635A' }}>
                                  Courses ({searchResults.courses.length})
                                </h4>
                                {searchResults.courses.map((course) => (
                                  <div
                                    key={course.id}
                                    onClick={() => {
                                      clearSearch();
                                      navigate(`/courses/${course.id}`);
                                    }}
                                    className="p-3 rounded-lg hover:bg-gray-50 cursor-pointer flex items-center gap-3"
                                    data-testid={`search-result-course-${course.id}`}
                                  >
                                    <CourseVisual course={course} className="w-12 h-12 rounded-lg flex-shrink-0" bg="#E8F5E9" />
                                    <div className="flex-1 min-w-0">
                                      <p className="font-medium truncate" style={{ color: '#163329' }}>{course.title}</p>
                                      <p className="text-xs truncate" style={{ color: '#5C635A' }}>
                                        By {course.teacher_name} • <Usd value={course.price} />
                                      </p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            {/* Products Results */}
                            {searchResults.products && searchResults.products.length > 0 && (
                              <div className="p-3 border-t" style={{ borderColor: '#E3E1DB' }}>
                                <h4 className="text-xs font-semibold uppercase px-2 mb-2" style={{ color: '#5C635A' }}>
                                  Products ({searchResults.products.length})
                                </h4>
                                {searchResults.products.map((product) => (
                                  <div
                                    key={product.id}
                                    onClick={() => {
                                      clearSearch();
                                      navigate(`/marketplace/${product.id}`);
                                    }}
                                    className="p-3 rounded-lg hover:bg-gray-50 cursor-pointer flex items-center gap-3"
                                    data-testid={`search-result-product-${product.id}`}
                                  >
                                    {product.image_url ? (
                                      <img src={product.image_url} alt={product.name} className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
                                    ) : (
                                      <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#FFF3E0' }}>
                                        <Package className="h-6 w-6" style={{ color: '#D86B47' }} />
                                      </div>
                                    )}
                                    <div className="flex-1 min-w-0">
                                      <p className="font-medium truncate" style={{ color: '#163329' }}>{product.name}</p>
                                      <p className="text-xs truncate" style={{ color: '#5C635A' }}>
                                        {product.category} • <Usd value={product.price} />
                                      </p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            {/* View All Results Link */}
                            <div className="p-3 border-t text-center" style={{ borderColor: '#E3E1DB' }}>
                              <button
                                onClick={() => {
                                  const query = searchQuery;
                                  clearSearch();
                                  navigate(`/courses?search=${encodeURIComponent(query)}`);
                                }}
                                className="text-sm font-medium hover:underline"
                                style={{ color: '#D86B47' }}
                              >
                                View all {searchResults.total} results
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
              
              <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4 justify-center items-center px-4">
                <Button
                  onClick={() => handleGetStarted()}
                  size="lg"
                  className="px-6 sm:px-8 w-full sm:w-auto"
                  data-testid="hero-learner-seller-button"
                >
                  <Play className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                  Tap here to be a Learner/Seller
                </Button>
                <Button
                  onClick={() => navigate('/courses')}
                  size="lg"
                  variant="outline"
                  className="px-6 sm:px-8 w-full sm:w-auto"
                  data-testid="explore-courses-button"
                >
                  <BookOpen className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                  Explore Courses
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Teacher Announcements Ribbon - Above Marketplace */}
      <AnnouncementRibbon announcements={announcements} />

      {/* Featured Products Ribbon - Only show if there are at least 6 real products */}
      {ribbonItems.length >= 6 && (
      <section className="py-6 sm:py-8" style={{ backgroundColor: '#163329' }}>
        <div className="mb-4 px-4 sm:px-6">
          <h3 
            className="text-center text-lg sm:text-xl md:text-2xl font-semibold text-white mb-2"
            style={{ fontFamily: 'Spectral, serif' }}
          >
            Featured Products & Skills
          </h3>
          <p className="text-center text-xs sm:text-sm" style={{ color: '#E3E1DB' }}>
            Explore our popular products from Insight Warehouse
          </p>
        </div>
        
        {/* Auto-scrolling Carousel with CSS animation */}
        <div 
          ref={ribbonRef}
          className="overflow-hidden ribbon-container"
          onTouchStart={handleRibbonInteractionStart}
          onTouchEnd={handleRibbonInteractionEnd}
          onMouseDown={handleRibbonInteractionStart}
          onMouseUp={handleRibbonInteractionEnd}
          onMouseLeave={handleRibbonInteractionEnd}
        >
          <div 
            className={`flex gap-4 sm:gap-5 py-4 px-4 ribbon-inner ${isRibbonPaused ? 'paused' : ''}`}
            style={{
              willChange: 'transform',
              width: 'fit-content'
            }}
          >
            {/* Duplicate items for seamless infinite loop */}
            {[...ribbonItems, ...ribbonItems].map((item, index) => (
              <div
                key={`${item.id}-${index}`}
                onClick={() => item.link && navigate(item.link)}
                className={`flex-shrink-0 ${item.link ? 'cursor-pointer' : 'cursor-default'}`}
                data-testid={`ribbon-item-${item.id}-${index}`}
              >
                <div 
                  className="relative w-40 sm:w-52 md:w-60 rounded-xl overflow-hidden shadow-lg"
                  style={{ border: '2px solid rgba(255,255,255,0.2)', backgroundColor: '#FFFFFF' }}
                >
                  <div className="w-full h-24 sm:h-28 md:h-32 bg-gray-100 flex items-center justify-center overflow-hidden">
                    {item.image ? (
                      <img
                        src={item.image.startsWith('/api') ? `${API}${item.image}` : item.image}
                        alt={item.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Package className="h-10 w-10" style={{ color: '#163329', opacity: 0.3 }} />
                    )}
                  </div>
                  <div className="p-2 sm:p-3">
                    {item.type && (
                      <div className="flex items-center gap-2 mb-1">
                        <span 
                          className="text-xs px-2 py-0.5 rounded-full"
                          style={{ 
                            backgroundColor: item.type === 'digital' ? '#E8F5E9' : '#FFF3E0',
                            color: item.type === 'digital' ? '#163329' : '#E65100'
                          }}
                        >
                          {item.type === 'digital' ? 'Digital' : 'Physical'}
                        </span>
                      </div>
                    )}
                    <h4 className="font-bold text-xs sm:text-sm line-clamp-1" style={{ color: '#163329' }}>{item.title}</h4>
                    <p className="text-xs line-clamp-1 mt-1" style={{ color: '#5C635A' }}>{item.subtitle}</p>
                    {item.price && (
                      <p className="text-sm font-bold mt-1" style={{ color: '#D86B47' }}>
                        <Usd value={Number(item.price)} />
                      </p>
                    )}
                    {item.price && (
                      <PriceCaption />
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <style>{`
          .ribbon-container {
            -webkit-overflow-scrolling: touch;
          }
          .ribbon-container::-webkit-scrollbar { display: none; }
          .ribbon-inner {
            animation: ribbonScroll 30s linear infinite;
          }
          .ribbon-inner.paused {
            animation-play-state: paused;
          }
          @keyframes ribbonScroll {
            0% {
              transform: translateX(0);
            }
            100% {
              transform: translateX(-50%);
            }
          }
        `}</style>
      </section>
      )}

      {/* Workshop Image Section */}
      <section className="py-4 sm:py-6 px-4 sm:px-6" style={{ backgroundColor: '#FAF9F6' }}>
        <div className="max-w-4xl mx-auto">
          <img
            src="https://customer-assets.emergentagent.com/job_edu-platform-304/artifacts/b99blawx_1002620953.jpg"
            alt="Skill Development Workshop"
            className="w-full max-w-full mx-auto rounded-lg shadow-lg object-contain"
          />
        </div>
      </section>

      {/* Home Learning Benefits Section */}
      <section className="pt-2 pb-2 px-4 sm:px-6" style={{ backgroundColor: '#FAF9F6' }}>
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-sm sm:text-base md:text-lg leading-relaxed px-2" style={{ color: '#5C635A', lineHeight: '1.8' }}>
            Did you ever realise that distance learning is so generally costly. As parents paying for their children's academic fees they get to face and pay the following critical expenses like accommodation, transport and foods all by themselves. So to avoid these, then home skill learning is appropriate. Insight Evolution encourages and offers Home Learning Kits on some Skills to learn at your own pace, avoid peer pressure and competition and be able to manage all home chores and learning at the same time.
          </p>
        </div>
      </section>

      {/* Insight Warehouse Section - Product Showcase - Only show if at least 6 products */}
      {(featuredProducts.length + newArrivals.length) >= 6 && (
      <section className="pt-4 pb-6 sm:pb-8" style={{ backgroundColor: '#F8F7F4' }} data-testid="insight-warehouse-section">
        {/* Header */}
        <div className="text-center mb-4 px-4">
          <div className="flex items-center justify-center gap-3 mb-3">
            <ShoppingBag className="h-8 w-8 sm:h-10 sm:w-10" style={{ color: '#163329' }} />
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
              Insight Warehouse
            </h2>
          </div>
          <p className="text-sm sm:text-base" style={{ color: '#5C635A' }}>
            Quality Learning Materials & Resources
          </p>
        </div>

        {/* Product Categories */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Featured Products */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg sm:text-xl font-semibold" style={{ color: '#163329', fontFamily: 'Spectral, serif' }}>
                Featured Products
              </h3>
              <button 
                onClick={() => navigate('/marketplace')}
                className="text-sm font-medium flex items-center gap-1 hover:underline"
                style={{ color: '#D86B47' }}
              >
                View All <ChevronRight className="h-4 w-4" />
              </button>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-4">
              {featuredProducts.map((slot) => (
                <div 
                  key={slot.slot_number}
                  onClick={() => slot.custom_link && navigate(slot.custom_link)}
                  className={`bg-white rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-all duration-300 group ${slot.custom_link ? 'cursor-pointer' : 'cursor-default'}`}
                  style={{ border: '1px solid #E3E1DB' }}
                  data-testid={`featured-product-${slot.slot_number}`}
                >
                  <div className="h-20 sm:h-24 flex items-center justify-center overflow-hidden">
                    {slot.custom_image ? (
                      <img
                        src={slot.custom_image.startsWith('/api') ? `${API}${slot.custom_image}` : slot.custom_image}
                        alt={slot.custom_title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-[#163329] to-[#2a5a47] flex items-center justify-center">
                        <Package className="h-8 w-8 sm:h-10 sm:w-10 text-white/80" />
                      </div>
                    )}
                  </div>
                  <div className="p-2">
                    <h4 className="font-semibold text-xs sm:text-sm mb-0.5 group-hover:text-[#D86B47] transition-colors line-clamp-1" style={{ color: '#163329' }}>
                      {slot.custom_title}
                    </h4>
                    {slot.custom_price && (
                      <><p className="font-bold text-xs sm:text-sm" style={{ color: '#D86B47' }}><Usd value={Number(slot.custom_price)} /></p><PriceCaption /></>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Essential Class */}
          <div className="mb-12">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg sm:text-xl font-semibold" style={{ color: '#163329', fontFamily: 'Spectral, serif' }}>
                Essential Class
              </h3>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-4">
              {newArrivals.map((slot) => (
                <div 
                  key={slot.slot_number}
                  onClick={() => slot.custom_link && navigate(slot.custom_link)}
                  className={`bg-white rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-all duration-300 group ${slot.custom_link ? 'cursor-pointer' : 'cursor-default'}`}
                  style={{ border: '1px solid #E3E1DB' }}
                  data-testid={`essential-class-${slot.slot_number}`}
                >
                  <div className="h-20 sm:h-24 flex items-center justify-center overflow-hidden">
                    {slot.custom_image ? (
                      <img
                        src={slot.custom_image.startsWith('/api') ? `${API}${slot.custom_image}` : slot.custom_image}
                        alt={slot.custom_title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-[#2E7D32] to-[#4CAF50] flex items-center justify-center">
                        <Package className="h-8 w-8 sm:h-10 sm:w-10 text-white/80" />
                      </div>
                    )}
                  </div>
                  <div className="p-2">
                    <h4 className="font-semibold text-xs sm:text-sm mb-0.5 group-hover:text-[#D86B47] transition-colors line-clamp-1" style={{ color: '#163329' }}>
                      {slot.custom_title}
                    </h4>
                    {slot.custom_price && (
                      <><p className="font-bold text-xs sm:text-sm" style={{ color: '#D86B47' }}><Usd value={Number(slot.custom_price)} /></p><PriceCaption /></>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Button */}
          <div className="text-center">
            <Button 
              onClick={() => navigate('/marketplace')}
              className="px-8 py-3 text-base sm:text-lg font-semibold rounded-full hover:scale-105 transition-transform"
              style={{ backgroundColor: '#163329', color: 'white' }}
              data-testid="view-all-warehouse-btn"
            >
              <ShoppingBag className="h-5 w-5 mr-2" />
              Explore Insight Warehouse
            </Button>
          </div>
        </div>
      </section>
      )}

      {/* Teacher Galleries Section */}
      {teacherGalleries.length > 0 && (
        <section className="py-8 sm:py-12 px-4 sm:px-6" style={{ backgroundColor: '#FAF9F6' }} data-testid="teacher-galleries-section">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.4 }}
              viewport={{ once: true }}
            >
              <h2 
                className="text-2xl sm:text-3xl font-bold text-center mb-2"
                style={{ fontFamily: 'Spectral, serif', color: '#163329' }}
              >
                Teacher Galleries
              </h2>
              <p className="text-center text-sm sm:text-base mb-8" style={{ color: '#5C635A' }}>
                Explore the work and portfolios of our talented teachers
              </p>
              
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {teacherGalleries.map((teacher) => (
                  <div 
                    key={teacher.id}
                    className="bg-white rounded-lg p-4 text-center shadow-sm border"
                    style={{ borderColor: '#E3E1DB' }}
                    data-testid={`teacher-gallery-card-${teacher.id}`}
                  >
                    {/* Teacher Avatar */}
                    <div className="w-16 h-16 mx-auto mb-3 rounded-full overflow-hidden" style={{ backgroundColor: '#E3E1DB' }}>
                      {teacher.avatar_url ? (
                        <img 
                          src={teacher.avatar_url} 
                          alt={teacher.name} 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Users className="h-8 w-8" style={{ color: '#5C635A' }} />
                        </div>
                      )}
                    </div>
                    
                    {/* Teacher Name */}
                    <h4 className="font-semibold text-sm mb-1 truncate" style={{ color: '#163329' }}>
                      {teacher.name} {teacher.surname ? teacher.surname.charAt(0) + '.' : ''}
                    </h4>
                    
                    {/* Image Count */}
                    <p className="text-xs mb-3" style={{ color: '#5C635A' }}>
                      {teacher.image_count} image{teacher.image_count !== 1 ? 's' : ''}
                    </p>
                    
                    {/* Gallery Button */}
                    {teacher.image_count >= 1 ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate(`/teacher/${teacher.id}/gallery`)}
                        className="w-full text-xs"
                        data-testid={`view-gallery-btn-${teacher.id}`}
                      >
                        Gallery
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled
                        className="w-full text-xs opacity-50"
                        data-testid={`gallery-unavailable-btn-${teacher.id}`}
                      >
                        Not available
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Teacher Feature */}
      <section className="py-6 sm:py-8 px-4 sm:px-6" style={{ backgroundColor: '#F0EFEA' }}>
        <div className="max-w-4xl mx-auto">
          <div className="text-center">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.4 }}
              viewport={{ once: true }}
              style={{ willChange: 'opacity' }}
            >
              <h2
                className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-4 sm:mb-6 px-2"
                style={{ fontFamily: 'Spectral, serif', color: '#163329' }}
              >
                Share Skill, Teach and Inspire
              </h2>
              <p className="text-sm sm:text-base md:text-lg mb-6 px-2" style={{ color: '#5C635A', lineHeight: '1.7' }}>
                Share your knowledge and skill with Learners, hustlers and entrepreneurs worldwide. You can share any kind of skills or mentorship either accredited or non-accredited. Create video lessons with workshops and classes, host live sessions if necessary, and earn revenue doing what you love. To share a skill and become a Trainer, Teacher, Mentor or Instructor, just press the button below to apply
              </p>
              <Button
                onClick={() => handleGetStarted('teacher')}
                size="lg"
                className="px-6 sm:px-8"
                data-testid="become-teacher-button"
              >
                Teacher, Trainer, Mentor, Creator
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-6 sm:py-8 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
            viewport={{ once: true }}
            style={{ willChange: 'opacity' }}
          >
            <h2
              className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-4 sm:mb-6 px-2"
              style={{ fontFamily: 'Spectral, serif', color: '#163329' }}
            >
              Ready to Start Your Learning Journey?
            </h2>
            <p className="text-sm sm:text-base md:text-lg mb-6 sm:mb-8 px-2" style={{ color: '#5C635A' }}>
              Join our community of Learners, Educators, Trainers, Mentors and Creators today
            </p>
            <Button
              onClick={() => handleGetStarted()}
              size="lg"
              className="px-8 sm:px-12"
              data-testid="cta-join-button"
            >
              Get Started
            </Button>
            
            {/* Stats - below join button */}
            <div className="flex flex-wrap justify-center gap-6 sm:gap-10 mt-6 sm:mt-8">
              <div className="text-center">
                <p className="text-lg sm:text-xl md:text-2xl font-bold" style={{ color: '#163329', fontFamily: 'Spectral, serif' }}>
                  {landingStats.views}
                </p>
                <p className="text-xs sm:text-sm" style={{ color: '#5C635A' }}>
                  Views
                </p>
              </div>
              <div className="text-center">
                <p className="text-lg sm:text-xl md:text-2xl font-bold" style={{ color: '#D86B47', fontFamily: 'Spectral, serif' }}>
                  {landingStats.enrollments}
                </p>
                <p className="text-xs sm:text-sm" style={{ color: '#5C635A' }}>
                  Enrollments
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Role Selection Modal */}
      <RoleSelectionModal
        open={showRoleSelection}
        onClose={() => setShowRoleSelection(false)}
        onSelectRole={handleRoleSelected}
      />

      <AuthModal
        open={showAuth}
        onClose={() => setShowAuth(false)}
        onSuccess={handleAuthSuccess}
        defaultRole={authDefaultRole}
        defaultTab={authDefaultRole === 'teacher' ? 'signup' : 'login'}
        lockRole={true}
      />

      {/* Exit Confirmation Dialog */}
      <Dialog open={showExitConfirm} onOpenChange={setShowExitConfirm}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ color: '#163329' }}>
              <AlertTriangle className="h-5 w-5" style={{ color: '#D86B47' }} />
              Exit Insight Evolution?
            </DialogTitle>
            <DialogDescription className="pt-2">
              Are you sure you want to leave? If so then Swipe left twice instantly then the App will exit. Then you can always come back anytime to explore our courses and products.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex justify-center">
            <Button
              variant="outline"
              onClick={handleExitCancel}
              className="w-full"
            >
              Stay
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Footer with Social Links */}
      <Footer />
    </div>
  );
};

export default LandingPage;